import React, { useEffect } from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import { ARTICLES } from '../components/ArticlesSection';
import TopNav from '../components/TopNav';
import Footer from '../components/Footer';

/**
 * Standalone page for an individual article. Lives at /articles/:slug.
 * Reads the slug from the URL, finds the matching article from the shared
 * ARTICLES list, and renders it with proper meta tags for SEO and sharing.
 */
export default function ArticlePage() {
  const { slug } = useParams();
  const article = ARTICLES.find(a => a.slug === slug);

  // Per-article SEO meta updates. We set document.title and the meta
  // description so each article looks like a real page to Google + social
  // crawlers (without needing react-helmet-async or SSR).
  useEffect(() => {
    if (!article) return;
    const prevTitle = document.title;
    const metaDesc = document.querySelector('meta[name="description"]');
    const prevDesc = metaDesc ? metaDesc.getAttribute('content') : null;

    document.title = `${article.title} — Etsy Fee Calculator`;
    if (metaDesc) metaDesc.setAttribute('content', article.summary);

    // Also update OG / Twitter so social shares of this URL show the
    // article-specific preview rather than the homepage's.
    const updateMeta = (selector, value) => {
      const el = document.querySelector(selector);
      if (el) el.setAttribute('content', value);
    };
    updateMeta('meta[property="og:title"]', article.title);
    updateMeta('meta[property="og:description"]', article.summary);
    updateMeta('meta[name="twitter:title"]', article.title);
    updateMeta('meta[name="twitter:description"]', article.summary);

    // Scroll to top on navigation (React Router doesn't do this automatically)
    window.scrollTo(0, 0);

    // Restore on unmount so navigating back doesn't leave article meta on the homepage
    return () => {
      document.title = prevTitle;
      if (metaDesc && prevDesc != null) metaDesc.setAttribute('content', prevDesc);
    };
  }, [article]);

  // Unknown slug → bounce to homepage. Could swap for a proper 404 later.
  if (!article) {
    return <Navigate to="/" replace />;
  }

  return (
    <>
      <TopNav />
      <main
        style={{
          maxWidth: '46rem',
          margin: '0 auto',
          padding: '2rem 1rem 3rem',
        }}
      >
        <Link
          to="/#articles"
          style={{
            display: 'inline-block',
            fontFamily: 'Inter, sans-serif',
            fontSize: '0.88rem',
            fontWeight: 600,
            color: '#1e3a8a',
            textDecoration: 'none',
            marginBottom: '1.25rem',
          }}
        >
          ← All articles
        </Link>

        <article
          style={{
            background: '#ffffff',
            border: '2px solid rgba(26, 26, 46, 0.12)',
            borderRadius: '0.75rem',
            padding: '1.75rem 1.85rem',
            boxShadow: '0 1px 2px rgba(26, 26, 46, 0.04), 0 8px 24px rgba(26, 26, 46, 0.06)',
          }}
        >
          {/* Tag + read time */}
          <div
            style={{
              display: 'flex',
              gap: '0.5rem',
              alignItems: 'center',
              marginBottom: '0.6rem',
              flexWrap: 'wrap',
            }}
          >
            <span
              style={{
                fontFamily: 'Inter, sans-serif',
                fontSize: '0.7rem',
                fontWeight: 700,
                color: article.tagColor,
                background: `${article.tagColor}18`,
                padding: '0.22rem 0.6rem',
                borderRadius: '999px',
                letterSpacing: '0.06em',
                textTransform: 'uppercase',
              }}
            >
              {article.tag}
            </span>
            <span
              style={{
                fontFamily: 'Inter, sans-serif',
                fontSize: '0.82rem',
                color: 'rgba(26, 26, 46, 0.62)',
              }}
            >
              {article.readTime}
            </span>
          </div>

          {/* Title */}
          <h1
            style={{
              fontFamily: '"Playfair Display", serif',
              fontSize: '2rem',
              fontWeight: 700,
              color: '#1a1a2e',
              lineHeight: 1.2,
              marginBottom: '0.65rem',
              letterSpacing: '-0.01em',
            }}
          >
            {article.title}
          </h1>

          {/* Summary as a lede paragraph */}
          <p
            style={{
              fontFamily: 'Inter, sans-serif',
              fontSize: '1.06rem',
              color: 'rgba(26, 26, 46, 0.72)',
              lineHeight: 1.6,
              marginBottom: '1.5rem',
              fontStyle: 'italic',
            }}
          >
            {article.summary}
          </p>

          {/* Body */}
          <div
            className="article-body"
            style={{
              fontFamily: 'Inter, sans-serif',
              fontSize: '1.02rem',
              color: 'rgba(26, 26, 46, 0.85)',
              lineHeight: 1.7,
            }}
          >
            {article.content}
          </div>

          {/* CTA back to calculator */}
          <div
            style={{
              marginTop: '2rem',
              paddingTop: '1.25rem',
              borderTop: '1px dashed rgba(26, 26, 46, 0.2)',
              display: 'flex',
              gap: '0.6rem',
              flexWrap: 'wrap',
            }}
          >
            <Link
              to="/"
              style={{
                padding: '0.55rem 1.1rem',
                background: '#1e3a8a',
                color: '#fefdf8',
                border: '2px solid #1a1a2e',
                borderRadius: '0.55rem',
                fontFamily: 'Inter, sans-serif',
                fontWeight: 700,
                fontSize: '0.95rem',
                cursor: 'pointer',
                textDecoration: 'none',
                boxShadow: '2px 3px 0 rgba(26, 26, 46, 0.18)',
                display: 'inline-block',
              }}
            >
              🧮 Try the calculator →
            </Link>
            <Link
              to="/#articles"
              style={{
                padding: '0.55rem 1.1rem',
                background: '#fefdf8',
                color: '#1a1a2e',
                border: '2px solid #1a1a2e',
                borderRadius: '0.55rem',
                fontFamily: 'Inter, sans-serif',
                fontWeight: 700,
                fontSize: '0.95rem',
                cursor: 'pointer',
                textDecoration: 'none',
                display: 'inline-block',
              }}
            >
              More articles
            </Link>
          </div>
        </article>

        {/* Related-articles strip — show 2 sibling articles */}
        <RelatedArticles currentSlug={slug} />
      </main>
      <Footer />
    </>
  );
}

// Picks 2 sibling articles to recommend at the bottom of an article page.
function RelatedArticles({ currentSlug }) {
  const others = ARTICLES.filter(a => a.slug !== currentSlug).slice(0, 2);
  if (others.length === 0) return null;
  return (
    <section
      style={{
        marginTop: '2.5rem',
      }}
    >
      <p
        style={{
          fontFamily: 'Inter, sans-serif',
          fontSize: '0.74rem',
          fontWeight: 700,
          color: 'rgba(26,26,46,0.55)',
          letterSpacing: '0.08em',
          textTransform: 'uppercase',
          marginBottom: '0.7rem',
        }}
      >
        Read next
      </p>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
          gap: '0.85rem',
        }}
      >
        {others.map(a => (
          <Link
            key={a.slug}
            to={`/articles/${a.slug}`}
            style={{
              background: '#fefdf8',
              border: '1.5px solid rgba(26, 26, 46, 0.12)',
              borderRadius: '0.625rem',
              padding: '0.85rem 1rem',
              textDecoration: 'none',
              color: 'inherit',
              display: 'block',
              transition: 'all 0.15s',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.borderColor = '#1e3a8a';
              e.currentTarget.style.transform = 'translateY(-1px)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.borderColor = 'rgba(26, 26, 46, 0.12)';
              e.currentTarget.style.transform = 'translateY(0)';
            }}
          >
            <span
              style={{
                fontFamily: 'Inter, sans-serif',
                fontSize: '0.66rem',
                fontWeight: 700,
                color: a.tagColor,
                background: `${a.tagColor}18`,
                padding: '0.15rem 0.45rem',
                borderRadius: '999px',
                letterSpacing: '0.06em',
                textTransform: 'uppercase',
                display: 'inline-block',
                marginBottom: '0.4rem',
              }}
            >
              {a.tag}
            </span>
            <h3
              style={{
                fontFamily: '"Playfair Display", serif',
                fontSize: '1.08rem',
                fontWeight: 600,
                color: '#1a1a2e',
                lineHeight: 1.3,
                margin: 0,
              }}
            >
              {a.title}
            </h3>
          </Link>
        ))}
      </div>
    </section>
  );
}
